import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminButton } from "@/components/admin/button";
import { Card } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Segmented } from "@/components/admin/segmented";
import { Tag } from "@/components/admin/tag";
import { useToast } from "@/components/ui/feedback";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { useSiteConfig, useUpdateSiteConfig } from "@/hooks/use-site";
import { ApprovalsTab } from "./users/approvals-tab";
import { AuditTab } from "./users/audit-tab";
import { SecurityTab } from "./users/security-tab";
import { TeamsTab } from "./users/teams-tab";
import { UsersList } from "./users/users-list";

type Tab =
	| "users"
	| "teams"
	| "roles"
	| "audit"
	| "security"
	| "approvals"
	| "settings";

interface RoleSpec {
	r: string;
	limit: string;
	count: number;
	color: string;
	desc: string;
	perms: string[];
}

const ROLES: RoleSpec[] = [
	{
		r: "Super Admin",
		limit: "2-3 max",
		count: 1,
		color: "var(--a-terracotta)",
		desc: "Accès complet sans restriction. Double validation (4-eyes) sur actions sensibles.",
		perms: ["admin.*", "system.*", "audit.read.all", "users.*"],
	},
	{
		r: "Admin",
		limit: "5-8",
		count: 1,
		color: "var(--a-info)",
		desc: "Supervision opérationnelle. Pas d'accès aux paramètres système critiques.",
		perms: ["orders.*", "catalog.*", "finance.read", "customers.*"],
	},
	{
		r: "Manager Logistique",
		limit: "1-2",
		count: 1,
		color: "var(--a-forest)",
		desc: "Pilote logistique : préparation, expédition, livraison.",
		perms: ["logistics.*", "suppliers.read", "orders.update.status"],
	},
	{
		r: "Manager Finance",
		limit: "1-2",
		count: 1,
		color: "var(--a-forest)",
		desc: "Trésorerie, règlements vendredi, exports SYSCOHADA.",
		perms: ["finance.*", "payments.*", "settlements.execute"],
	},
	{
		r: "Manager Support",
		limit: "1-2",
		count: 1,
		color: "var(--a-forest)",
		desc: "Pilotage CSR : tickets, retours, qualité service client.",
		perms: ["support.*", "tickets.escalate", "refunds.approve"],
	},
	{
		r: "CSR",
		limit: "20-25",
		count: 1,
		color: "var(--a-ochre)",
		desc: "Service client : tickets, retours, gestes commerciaux ≤ 5 000 F.",
		perms: ["tickets.*", "customers.read", "orders.read", "refunds.create"],
	},
	{
		r: "Logistique",
		limit: "30-40",
		count: 1,
		color: "var(--a-ochre)",
		desc: "Préparation, scan, validation colis, tournées.",
		perms: ["tours.*", "scan.product", "orders.update.shipped"],
	},
	{
		r: "Marketing",
		limit: "8-10",
		count: 1,
		color: "var(--a-ochre)",
		desc: "Promotions, CMS, segmentation, campagnes.",
		perms: ["promotions.*", "cms.*", "segments.*"],
	},
];

const INTEGRATIONS: [string, string][] = [
	["MTN Mobile Money API", "connected"],
	["Orange Money API", "connected"],
	["Stripe (cartes)", "connected"],
	["Meilisearch", "connected"],
	["NEXAH SMS", "connected"],
	["WhatsApp Business API", "connected"],
	["Vault (secrets)", "connected"],
];

export function AdminAdministrationPage() {
	const [tab, setTab] = useState<Tab>("users");
	const navigate = useNavigate();

	return (
		<>
			<PageHead
				title="Administration système"
				sub="Utilisateurs internes · rôles · journal d'audit · paramètres"
				actions={
					<>
						{tab === "users" && (
							<AdminButton
								variant="primary"
								onClick={() => navigate("/administration/utilisateurs/nouveau")}
								data-testid="admin-create-user"
							>
								<AdminIcon n="plus" s={14} />
								Nouvel utilisateur
							</AdminButton>
						)}
						{tab === "audit" && (
							<AdminButton variant="ghost">
								<AdminIcon n="download" s={14} />
								Export journal
							</AdminButton>
						)}
					</>
				}
			/>

			<Segmented
				value={tab}
				onChange={setTab}
				className="mb-4"
				options={[
					{ value: "users", label: "Utilisateurs" },
					{ value: "teams", label: "Équipes" },
					{ value: "roles", label: "Rôles & permissions (8)" },
					{ value: "approvals", label: "Approbations (4-eyes)" },
					{ value: "audit", label: "Journal d'audit" },
					{ value: "security", label: "Sécurité opé." },
					{ value: "settings", label: "Paramètres" },
				]}
			/>

			{tab === "users" && <UsersList embed />}
			{tab === "teams" && <TeamsTab />}
			{tab === "approvals" && <ApprovalsTab />}
			{tab === "security" && <SecurityTab />}


			{tab === "roles" && (
				<div className="grid-2e">
					{ROLES.map((role) => (
						<Card key={role.r}>
							<div className="card-b">
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 10,
										marginBottom: 8,
									}}
								>
									<span
										style={{
											width: 8,
											height: 8,
											borderRadius: "50%",
											background: role.color,
										}}
									/>
									<strong
										style={{
											fontFamily: "var(--a-font-display)",
											fontSize: 14,
										}}
									>
										{role.r}
									</strong>
									<Tag variant="neutral" noDot style={{ marginLeft: "auto" }}>
										{role.count} utilisateur
									</Tag>
								</div>
								<p className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
									{role.desc}
								</p>
								<div className="eyebrow" style={{ marginBottom: 6 }}>
									Limite recommandée · {role.limit}
								</div>
								<div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
									{role.perms.map((p) => (
										<span
											key={p}
											className="mono"
											style={{
												fontSize: 10,
												padding: "2px 6px",
												background: "var(--a-surface-2)",
												borderRadius: 3,
											}}
										>
											{p}
										</span>
									))}
								</div>
							</div>
						</Card>
					))}
				</div>
			)}

			{tab === "audit" && <AuditTab />}


			{tab === "settings" && <SettingsTab />}
		</>
	);
}

function SettingsTab() {
	const toast = useToast();
	const { data: cfg, isLoading } = useSiteConfig();
	const update = useUpdateSiteConfig();

	const [bannerText, setBannerText] = useState("");

	useEffect(() => {
		if (cfg?.bannerText) setBannerText(cfg.bannerText);
	}, [cfg?.bannerText]);

	if (isLoading) {
		return (
			<div style={{ padding: 20, opacity: 0.6 }}>
				Chargement des paramètres…
			</div>
		);
	}

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
			<Card>
				<div className="card-h">
					<div>
						<h3>Activation &amp; Fonctionnalités Plateforme</h3>
						<p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
							Activez ou désactivez les fonctionnalités sans remise en ligne ni
							redéploiement.
						</p>
					</div>
				</div>
				<div
					className="card-b"
					style={{ display: "flex", flexDirection: "column", gap: 16 }}
				>
					{/* Toggle Coming Soon */}
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<div>
							<strong>Mode « Coming Soon »</strong>
							<div style={{ fontSize: 12, opacity: 0.7 }}>
								Affiche la page de compte à rebours au lieu de la boutique.
							</div>
						</div>
						<button
							type="button"
							onClick={() =>
								update.mutate(
									{ comingSoonEnabled: !cfg?.comingSoonEnabled },
									{
										onSuccess: () =>
											toast.success("Mode « Coming Soon » mis à jour."),
										onError: (e) =>
											toast.error(
												extractErrorMessage(e, "Mise à jour impossible"),
											),
									},
								)
							}
							disabled={update.isPending}
							style={{
								cursor: "pointer",
								width: 50,
								height: 26,
								borderRadius: 999,
								border: "none",
								background: cfg?.comingSoonEnabled ? "#C6563A" : "#cbd5e1",
								position: "relative",
								transition: "background .15s",
							}}
						>
							<span
								style={{
									position: "absolute",
									top: 3,
									left: cfg?.comingSoonEnabled ? 27 : 3,
									width: 20,
									height: 20,
									borderRadius: "50%",
									background: "#fff",
									transition: "left .15s",
								}}
							/>
						</button>
					</div>

					<hr
						style={{ border: "none", borderTop: "1px solid var(--a-border)" }}
					/>

					{/* Toggle Maintenance */}
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<div>
							<strong>Mode Maintenance</strong>
							<div style={{ fontSize: 12, opacity: 0.7 }}>
								Bloque les commandes et affiche un message de maintenance.
							</div>
						</div>
						<button
							type="button"
							onClick={() =>
								update.mutate(
									{ maintenanceEnabled: !cfg?.maintenanceEnabled },
									{
										onSuccess: () =>
											toast.success("Mode maintenance mis à jour."),
										onError: (e) =>
											toast.error(
												extractErrorMessage(e, "Mise à jour impossible"),
											),
									},
								)
							}
							disabled={update.isPending}
							style={{
								cursor: "pointer",
								width: 50,
								height: 26,
								borderRadius: 999,
								border: "none",
								background: cfg?.maintenanceEnabled ? "#C6563A" : "#cbd5e1",
								position: "relative",
								transition: "background .15s",
							}}
						>
							<span
								style={{
									position: "absolute",
									top: 3,
									left: cfg?.maintenanceEnabled ? 27 : 3,
									width: 20,
									height: 20,
									borderRadius: "50%",
									background: "#fff",
									transition: "left .15s",
								}}
							/>
						</button>
					</div>

					<hr
						style={{ border: "none", borderTop: "1px solid var(--a-border)" }}
					/>

					{/* Toggle Ventes Flash */}
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<div>
							<strong>Module Ventes Flash (Deals)</strong>
							<div style={{ fontSize: 12, opacity: 0.7 }}>
								Affiche la section Ventes Flash sur l'accueil.
							</div>
						</div>
						<button
							type="button"
							onClick={() =>
								update.mutate(
									{ dealsEnabled: !cfg?.dealsEnabled },
									{
										onSuccess: () =>
											toast.success("Module Ventes Flash mis à jour."),
										onError: (e) =>
											toast.error(
												extractErrorMessage(e, "Mise à jour impossible"),
											),
									},
								)
							}
							disabled={update.isPending}
							style={{
								cursor: "pointer",
								width: 50,
								height: 26,
								borderRadius: 999,
								border: "none",
								background: cfg?.dealsEnabled !== false ? "#2d6a4f" : "#cbd5e1",
								position: "relative",
								transition: "background .15s",
							}}
						>
							<span
								style={{
									position: "absolute",
									top: 3,
									left: cfg?.dealsEnabled !== false ? 27 : 3,
									width: 20,
									height: 20,
									borderRadius: "50%",
									background: "#fff",
									transition: "left .15s",
								}}
							/>
						</button>
					</div>

					<hr
						style={{ border: "none", borderTop: "1px solid var(--a-border)" }}
					/>

					{/* Bannière d'Annonce */}
					<div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								marginBottom: 10,
							}}
						>
							<div>
								<strong>Bannière d'Annonce Globale</strong>
								<div style={{ fontSize: 12, opacity: 0.7 }}>
									Affiche un bandeau d'information au sommet du site client.
								</div>
							</div>
							<button
								type="button"
								onClick={() =>
									update.mutate(
										{ bannerEnabled: !cfg?.bannerEnabled },
										{
											onSuccess: () =>
												toast.success("Bannière d’annonce mise à jour."),
											onError: (e) =>
												toast.error(
													extractErrorMessage(e, "Mise à jour impossible"),
												),
										},
									)
								}
								disabled={update.isPending}
								style={{
									cursor: "pointer",
									width: 50,
									height: 26,
									borderRadius: 999,
									border: "none",
									background: cfg?.bannerEnabled ? "#2d6a4f" : "#cbd5e1",
									position: "relative",
									transition: "background .15s",
								}}
							>
								<span
									style={{
										position: "absolute",
										top: 3,
										left: cfg?.bannerEnabled ? 27 : 3,
										width: 20,
										height: 20,
										borderRadius: "50%",
										background: "#fff",
										transition: "left .15s",
									}}
								/>
							</button>
						</div>
						<div style={{ display: "flex", gap: 8, marginTop: 8 }}>
							<input
								type="text"
								value={bannerText}
								onChange={(e) => setBannerText(e.target.value)}
								placeholder="Ex: 🚚 Livraison offerte à partir de 20 000 FCFA !"
								style={{
									flex: 1,
									padding: "8px 12px",
									borderRadius: 6,
									border: "1px solid var(--a-border)",
									fontSize: 13,
								}}
							/>
							<AdminButton
								variant="primary"
								onClick={() =>
									update.mutate(
										{ bannerText },
										{
											onSuccess: () =>
												toast.success(
													"Texte de la bannière enregistré avec succès !",
												),
											onError: (e) =>
												toast.error(
													extractErrorMessage(e, "Enregistrement impossible"),
												),
										},
									)
								}
								disabled={update.isPending}
							>
								{update.isPending ? "Enregistrement…" : "Enregistrer"}
							</AdminButton>
						</div>
					</div>
				</div>
			</Card>

			<div className="grid-3">
				<Card>
					<div className="card-h">
						<div>
							<h3>Sécurité</h3>
						</div>
					</div>
					<div className="card-b">
						<div className="info-row">
							<span className="l">MFA obligatoire (Super Admin/Admin)</span>
							<span className="v">
								<Tag variant="success" noDot>
									Activé
								</Tag>
							</span>
						</div>
						<div className="info-row">
							<span className="l">Politique mot de passe</span>
							<span className="v">12 car., complexe</span>
						</div>
						<div className="info-row">
							<span className="l">Session inactive (max)</span>
							<span className="v">60 min</span>
						</div>
						<div className="info-row">
							<span className="l">Sessions simultanées</span>
							<span className="v">3 par compte</span>
						</div>
						<div className="info-row">
							<span className="l">Rate limiting login</span>
							<span className="v">60 req / min</span>
						</div>
					</div>
				</Card>
				<Card>
					<div className="card-h">
						<div>
							<h3>Intégrations</h3>
						</div>
					</div>
					<div className="card-b">
						{INTEGRATIONS.map(([n, s], i) => (
							<div
								key={n}
								style={{
									display: "flex",
									justifyContent: "space-between",
									padding: "7px 0",
									borderBottom:
										i < INTEGRATIONS.length - 1
											? "1px solid var(--a-border)"
											: "none",
								}}
							>
								<span style={{ fontSize: 12 }}>{n}</span>
								<Tag variant="success">
									{s === "connected" ? "Connectée" : s}
								</Tag>
							</div>
						))}
					</div>
				</Card>
				<Card>
					<div className="card-h">
						<div>
							<h3>Système</h3>
						</div>
					</div>
					<div className="card-b">
						<div className="info-row">
							<span className="l">Environnement</span>
							<span className="v">
								<Tag variant="warning">Production</Tag>
							</span>
						</div>
						<div className="info-row">
							<span className="l">Version backend</span>
							<span className="v mono">v2.4.18</span>
						</div>
						<div className="info-row">
							<span className="l">Version dashboard</span>
							<span className="v mono">v1.12.3</span>
						</div>
						<div className="info-row">
							<span className="l">Dernier déploiement</span>
							<span className="v">12 mai · 03:14</span>
						</div>
						<div className="info-row">
							<span className="l">Uptime 90j</span>
							<span className="v">99,87%</span>
						</div>
						<div className="info-row">
							<span className="l">Hub Docker Swarm</span>
							<span className="v">7 nœuds</span>
						</div>
					</div>
				</Card>
			</div>
		</div>
	);
}
